import type { HeartbeatVerb } from '../api/contract.ts'

/**
 * The heartbeat as console-api serves it (ASK-012), and the reasons it is NOT folded into
 * `health.ts`.
 *
 * `health.ts` derives staleness from `manifest.pollIntervalSeconds + pollJitterSeconds`,
 * because that is the schedule a relay reads for the DOWNWARD fetch loop and we can only infer
 * when it should next appear. The heartbeat channel is not that: `nextHeartbeatBy` is a value
 * WE set per call and the relay obeys. Re-deriving it from the poll window would invent a
 * schedule we were handed, and would go wrong the moment a ring is advanced - because "advance
 * to ring 2" IS "set ring 2's next call-back", so a rollout deliberately moves these and
 * leaves the poll interval alone. Two channels, two schedules, two signals, side by side.
 *
 * THESE TYPES MIRROR THE WIRE, NOT THE RELAY EXCHANGE. An earlier version modelled
 * `{ received, answered, receivedAt }` from `openapi.yaml`'s relay-facing schemas, which was a
 * consumer guessing at a shape nobody had published. console-api's read is flatter and it is
 * the right shape for a console; every field below is named after one in
 * `docs/plan/contracts/console/fixtures/`, so the fixtures join to this model without
 * translation and a change upstream shows up as a type error rather than a silent mismatch.
 *
 * TWO THINGS ARE COMPUTED SERVER-SIDE ON PURPOSE and must not be recomputed here. `overdue`
 * and `expired` are answered against the fleet's clock, because an operator whose laptop clock
 * has drifted would otherwise see a fleet-wide outage or a fleet-wide all-clear depending on
 * which way it drifted. A browser comparing timestamps against `Date.now()` is the defect.
 */

/** `GET /console/v1/deployments/{id}/heartbeat`. */
export interface HeartbeatState {
  deploymentId: string
  /**
   * Carried explicitly rather than inferred from a missing timestamp, so a relay that has
   * never called is distinguishable from a serialisation that dropped a field.
   */
  everHeartbeated: boolean
  /** Computed against the FLEET's clock. Never recompute from the browser. */
  overdue: boolean
  /** Absent until the first beat arrives. */
  lastHeartbeatReceivedAt?: string
  /** The call-back WE set. Absent until there has been a beat to answer. */
  nextHeartbeatBy?: string
  /** The restart signal. Absent until a beat has carried one. */
  processStartedAt?: string
  lastRestartAt?: string
  /**
   * OPTIONAL, AND ABSENT IS NOT ZERO: absent means this relay does not track restarts, and
   * rendering it as 0 reports a deployment as never having restarted when it has never said.
   */
  reportedRestartCount?: number
  /** OUR count of restarts we observed. Always present; zero genuinely means we saw none. */
  observedRestartCount: number
  /** Planes where the relay reports a version ahead of the fleet pointer. */
  reportsAheadPlanes: string[]
}

/** One row of `GET /deployments/{id}/restarts`, newest first by `observedAt`. */
export interface RestartRecord {
  /** The process this row is about - the relay's clock. */
  processStartedAt: string
  /** When WE saw it. The ordering key, because a relay offline can tell us late. */
  observedAt: string
  /** Absent when the relay does not track it. Absent is not zero here too. */
  reportedRestartCount?: number
}

/** Whether the fleet decided this, or a person forced it. */
export type InstructionSource = 'decision-tree' | 'override'

/** One row of `GET /deployments/{id}/instructions` - the SERVE RECORD. */
export interface IssuedInstruction {
  instructionId: string
  verb: HeartbeatVerb
  /**
   * An override is a person forcing a scenario, not the fleet deciding something, and the two
   * must not render alike - a human reading the log needs to see that someone chose it.
   */
  source: InstructionSource
  issuedAt: string
  expiresAt: string
  /** Computed against the FLEET's clock. Never recompute from the browser. */
  expired: boolean
  target?: string
  /**
   * Free text, and the contract is explicit that this is the ONE safe place for it: it travels
   * downward and is written by us, so there is no customer value to leak.
   */
  reason?: string
}

// ---------------------------------------------------------------------------
// Absent is not zero
// ---------------------------------------------------------------------------

/**
 * `NOT_REPORTED` is a value, deliberately, rather than `null` or `0`.
 *
 * NthAI.md: when we cannot answer we say so and say what it would take. A dashboard cell wants
 * a number, and the two ways of not having one - "this relay does not track restarts" and
 * "this relay has restarted zero times" - are different facts about the world. Returning `0`
 * for the first reports a deployment as never having restarted when it has never said.
 * Returning `null` invites `?? 0` at the call site, which is the same bug one line later.
 *
 * A caller must therefore branch, and TypeScript makes it do so.
 */
export const NOT_REPORTED = 'not-reported' as const
export type NotReported = typeof NOT_REPORTED

/**
 * The relay's OWN restart count, or the fact that it does not keep one.
 *
 * THERE IS DELIBERATELY NO `fleetRestartTotal()` HERE, and this is the place someone would add
 * one. A fleet-level figure summed or averaged over this has a denominator nobody chose:
 * relays that do not report restarts leave the numerator while remaining deployments, so "mean
 * restarts per deployment" silently means "mean across the ones that happen to track it". SQL
 * does the same one layer down - SUM and AVG skip NULLs, COUNT(*) does not - so the figure
 * disagrees with itself depending which side computes it.
 *
 * WHAT WOULD CHANGE THAT DECISION, because a refusal without one is an opinion someone later
 * has to argue with rather than read: console-api exposing a per-deployment REPORTING flag, so
 * the denominator is a fact we were told rather than inferred from absences; or a screen
 * rendering the figure and its coverage side by side, in which case the aggregate is fine and
 * this comment and its guard should be deleted together.
 *
 * The argument is fleet-api-0a's, stated as an argument and not as a measurement of our data.
 * `heartbeat.aggregate.test.ts` is the tripwire, and it exists because a documented refusal
 * with no test behind it is exactly the shape this estate keeps producing.
 */
export function restartsReported(state: HeartbeatState | RestartRecord): number | NotReported {
  const n = state.reportedRestartCount
  return typeof n === 'number' ? n : NOT_REPORTED
}

/**
 * OUR count of restarts we observed. Always a number, and that number is NOT interchangeable
 * with the relay's.
 *
 * `observedRestartCount === 0` does not mean "never restarted". It means "we observed none",
 * and those differ exactly when heartbeats were lost - which is the case the count exists for.
 * So when `reportedRestartCount` is absent, the honest cell is still "not reported": a number
 * we produced about our own blindness must not be promoted into a claim about their behaviour.
 *
 * Where both exist the GAP is the signal. Reported 9 against observed 3 means six restarts
 * happened while we were not listening, which is stronger and more actionable than either
 * number alone - so they render side by side and are never reconciled into one figure.
 */
export function restartsObserved(state: HeartbeatState): number {
  return state.observedRestartCount
}

/** True when the two counts disagree, which means beats were missed rather than that one is wrong. */
export function restartCountsDisagree(state: HeartbeatState): boolean {
  const reported = restartsReported(state)
  return reported !== NOT_REPORTED && reported !== state.observedRestartCount
}

// ---------------------------------------------------------------------------
// The schedule WE set
// ---------------------------------------------------------------------------

/**
 * `never-called` is not `overdue`, and the distinction is the whole point of the screen.
 *
 * A deployment that called and then stopped has told us something real and we can say when.
 * One that has never called has told us nothing, and the honest cell is "not reported", not a
 * red tick and not an age of zero. Silence in a deployment-initiated architecture is the only
 * evidence there is, so the console must not manufacture more of it than it has.
 */
export type HeartbeatSchedule = 'never-called' | 'awaited' | 'overdue'

export function scheduleOf(state: HeartbeatState | null): HeartbeatSchedule {
  if (!state || !state.everHeartbeated) return 'never-called'
  // `overdue` is the fleet's answer, not ours. A relay MAY always call early, so early is
  // never a fault - only late is, and only the server is entitled to say which.
  return state.overdue ? 'overdue' : 'awaited'
}

// ---------------------------------------------------------------------------
// Restart history
// ---------------------------------------------------------------------------

/**
 * A crash loop, which is what the history exists to make visible.
 *
 * A count and a latest timestamp describe a relay restarting four times in eleven minutes and
 * a healthy one that restarted four times over a year IDENTICALLY. The rows are the difference.
 *
 * Ordered by `observedAt`, OUR clock, not the relay's - a relay that was offline tells us late,
 * so its `processStartedAt` can be older than a row we saw before it.
 */
export function restartsWithin(records: RestartRecord[], minutes: number, now: Date): RestartRecord[] {
  const floor = now.getTime() - minutes * 60_000
  return records.filter((r) => Date.parse(r.observedAt) >= floor)
}

// ---------------------------------------------------------------------------
// The work list
// ---------------------------------------------------------------------------

/**
 * Instructions still worth showing as outstanding.
 *
 * `expired` is server-computed and simply read here. An instruction a relay picks up three days
 * late is usually wrong, so a relay discards what expired rather than executing a stale work
 * list against a fleet that has moved; a console listing expired instructions as still-pending
 * would show work nobody is ever going to do.
 */
export function liveInstructions(instructions: IssuedInstruction[]): IssuedInstruction[] {
  return instructions.filter((i) => !i.expired)
}

/**
 * True when there is nothing outstanding.
 *
 * AN EMPTY WORK LIST IS THE HEALTHY STEADY STATE. The contract says so in terms - it is not an
 * error and must not be rendered as one. A named predicate rather than a bare `.length === 0`
 * at a call site, precisely so a screen reads `isSteadyState(...)` and nobody wires an empty
 * array to a warning colour on a Friday.
 */
export function isSteadyState(instructions: IssuedInstruction[]): boolean {
  return liveInstructions(instructions).length === 0
}

/**
 * THERE IS NO `wasExecuted()` AND THERE CANNOT BE ONE YET.
 *
 * This endpoint is the SERVE RECORD: what the fleet TOLD a relay, not what the relay DID.
 * There is no `ackedAt` on the wire and fleet-api declined to add one, because nothing writes
 * it - a column that is always null renders as "nothing has been done" when it means "we do
 * not track this", which is the absent-is-not-zero defect in its most misleading form.
 *
 * Positive confirmation that a downward push was honoured arrives as `appliedVersions` on a
 * later beat, which the contract calls the only positive confirmation there is. When execution
 * outcomes land - done, failed, skipped, not-attempted - they get their own function here and
 * `not-attempted` will need rendering distinctly, because it means an earlier instruction
 * failed and the relay deliberately stopped: neither a failure nor a success.
 */
export function isOverride(instruction: IssuedInstruction): boolean {
  return instruction.source === 'override'
}
